import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Phone, Calendar, Sparkles, Download, ExternalLink, X } from "lucide-react";
import type { LeadWithGenerations } from "@/types/firestore";
import { Timestamp } from "firebase/firestore";
import { format } from "date-fns";

interface LeadDetailSheetProps {
  lead: LeadWithGenerations | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRemix: (lead: LeadWithGenerations) => void;
}

export function LeadDetailSheet({ lead, open, onOpenChange, onRemix }: LeadDetailSheetProps) {
  if (!lead) return null;

  const latestGeneration = lead.generations[lead.generations.length - 1];
  // Handle both real Firestore Timestamps and mock Timestamps from API compat layer
  const createdAt = lead.created_at && typeof lead.created_at === 'object' && 'toDate' in lead.created_at
    ? lead.created_at.toDate()
    : new Date(lead.created_at);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <SheetTitle className="text-left">Lead Details</SheetTitle>
          <SheetClose asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </SheetClose>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Generated Image */}
          {latestGeneration && (
            <div className="relative aspect-video overflow-hidden rounded-lg">
              <img
                src={latestGeneration.generated_image_path}
                alt="Generated renovation"
                className="h-full w-full object-cover"
              />
              <div className="absolute bottom-3 right-3 flex gap-2">
                <Button size="sm" variant="secondary" className="h-8 gap-1.5">
                  <Download className="h-3 w-3" />
                  Download
                </Button>
              </div>
            </div>
          )}

          {/* Client Info */}
          <div className="rounded-lg border border-border/50 bg-muted/30 p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold">{lead.client_name}</h3>
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>{lead.client_phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>{format(createdAt, "PPP 'at' p")}</span>
                  </div>
                </div>
              </div>
              <Badge
                variant="outline"
                className={
                  lead.verification_status === "verified"
                    ? "text-green-600 bg-green-50 border-green-200"
                    : lead.verification_status === "pending"
                    ? "text-yellow-600 bg-yellow-50 border-yellow-200"
                    : "text-gray-600 bg-gray-50 border-gray-200"
                }
              >
                {lead.verification_status === "verified"
                  ? "Verified"
                  : lead.verification_status === "pending"
                  ? "Pending"
                  : "Unverified"}
              </Badge>
            </div>
          </div>

          <Separator />

          {/* Room Analysis */}
          <div>
            <h4 className="font-semibold mb-3">AI Room Analysis</h4>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Badge variant="secondary">{lead.gemini_analysis?.room_type || 'Unknown'}</Badge>
                <Badge variant={lead.gemini_analysis?.room_state === "Studs" ? "destructive" : "default"}>
                  {lead.gemini_analysis?.room_state || 'Unknown'}
                </Badge>
              </div>
              {lead.gemini_analysis?.materials && lead.gemini_analysis.materials.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Detected Materials</p>
                  <div className="flex flex-wrap gap-1.5">
                    {lead.gemini_analysis.materials.map((material) => (
                      <Badge key={material} variant="outline" className="text-xs">
                        {material}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {lead.gemini_analysis?.notes && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm text-foreground">{lead.gemini_analysis.notes}</p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Design Parameters */}
          {latestGeneration && (
            <div>
              <h4 className="font-semibold mb-3">Design Parameters</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="text-xs text-muted-foreground">Style</p>
                  <p className="font-medium">{latestGeneration.prompt_parameters.style}</p>
                </div>
                {latestGeneration.prompt_parameters.floor && (
                  <div className="rounded-lg bg-muted/50 p-3">
                    <p className="text-xs text-muted-foreground">Flooring</p>
                    <p className="font-medium">{latestGeneration.prompt_parameters.floor}</p>
                  </div>
                )}
                {latestGeneration.prompt_parameters.walls && (
                  <div className="rounded-lg bg-muted/50 p-3">
                    <p className="text-xs text-muted-foreground">Walls</p>
                    <p className="font-medium">{latestGeneration.prompt_parameters.walls}</p>
                  </div>
                )}
                {latestGeneration.prompt_parameters.cabinets && (
                  <div className="rounded-lg bg-muted/50 p-3">
                    <p className="text-xs text-muted-foreground">Cabinets</p>
                    <p className="font-medium">{latestGeneration.prompt_parameters.cabinets}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button className="flex-1 gap-2" onClick={() => onRemix(lead)}>
              <Sparkles className="h-4 w-4" />
              Remix Design
            </Button>
            <Button variant="outline" className="flex-1 gap-2">
              <ExternalLink className="h-4 w-4" />
              Book Consultation
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
